import type { CodeRuleAuditConcern, CodeRuleAuditResult, ConcernSeverity } from "@/api/codeRuleAudits";
import type { BackgroundAuditState } from "./useBackgroundCodeRuleAudit";

export type DockTone = "idle" | "checking" | "clear" | "medium" | "high" | "failed";

export type DockStatus = {
  tone: DockTone;
  label: string;
  busy: boolean;
  highCount: number;
  mediumCount: number;
};

const KEY_SEPARATOR = "\u0000";

const SEVERITY_ORDER: Record<ConcernSeverity, number> = { high: 0, medium: 1, low: 2 };

const normaliseExcerpt = (text: string) => text.replace(/\s+/g, " ").trim().toLowerCase();

export const findingKey = (concern: CodeRuleAuditConcern) =>
  [concern.codeType, [...concern.relatedCodes].sort().join(","), normaliseExcerpt(concern.ruleExcerpt)].join(
    KEY_SEPARATOR,
  );

export const sortConcerns = (concerns: readonly CodeRuleAuditConcern[]) =>
  [...concerns].sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);

const countOf = (result: CodeRuleAuditResult, severity: ConcernSeverity) =>
  result.concerns.filter((concern) => concern.severity === severity).length;

function resultLabel(highCount: number, mediumCount: number) {
  const parts = [
    ...(highCount > 0 ? [`${highCount} high`] : []),
    ...(mediumCount > 0 ? [`${mediumCount} medium`] : []),
  ];
  return parts.length > 0 ? `Audit · ${parts.join(", ")}` : "Audit · no concerns found";
}

export function dockStatus({ phase, result }: BackgroundAuditState): DockStatus {
  if (phase === "failed")
    return { tone: "failed", label: "Audit · unchecked", busy: false, highCount: 0, mediumCount: 0 };
  if (phase === "idle") return { tone: "idle", label: "Audit", busy: false, highCount: 0, mediumCount: 0 };
  if (result === null)
    return { tone: "checking", label: "Audit · checking…", busy: true, highCount: 0, mediumCount: 0 };

  const highCount = countOf(result, "high");
  const mediumCount = countOf(result, "medium");
  const busy = phase === "checking";
  const tone = highCount > 0 ? "high" : mediumCount > 0 ? "medium" : "clear";
  const label = resultLabel(highCount, mediumCount);
  return { tone, label: busy ? `${label} · checking…` : label, busy, highCount, mediumCount };
}

export const newHighKeys = (concerns: readonly CodeRuleAuditConcern[], alreadyPeeked: ReadonlySet<string>) =>
  concerns
    .filter((concern) => concern.severity === "high")
    .map(findingKey)
    .filter((key) => !alreadyPeeked.has(key));
